import { colors } from "@areahub/theme";
import { Box, Flex, HStack, useDisclosure } from "@chakra-ui/react";
import { Button, ButtonLink_T, Button_T } from "@ui/components/button";
import { Link, Link_T } from "@ui/components/link";
import { PropsWithChildren } from "react";
import { DefaultMobileMenu } from "./default-mobile-menu";
import { HamburgerButton } from "./hamburger-button";
import { LinkedLogo } from "./linked-logo";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { getWebUrl } from "@areahub/config";
import {
  IndustriesDropdownMenu,
  IndustriesMenu,
  SolutionsDropdownMenu,
} from "./industries-dropdown-menu";
import { IoMdPerson } from "react-icons/io";

export interface LinkWithLabel_T extends Link_T {
  label: string;
  isInternal?: boolean;
  industriesMenu?: IndustriesMenu;
  industriesMenus?: IndustriesMenu[];
}

export interface NavContent_T extends PropsWithChildren<{}> {
  buttons?: Array<Button_T & { isInternal?: boolean }>;
  links?: LinkWithLabel_T[];
  mobileLinks?: LinkWithLabel_T[];
}

const MobileNavContent = ({ mobileLinks }: NavContent_T) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  return (
    <>
      {/* Main navigational bar */}
      <Flex
        bg={colors.enterpriseColors.blue["500"]}
        align="center"
        justify="space-between"
        className="nav-content__mobile"
        display={{
          base: "flex",
          lg: "none",
        }}
        p="10px"
      >
        <LinkedLogo h="30px" />
        <HamburgerButton isOpen={isOpen} onClick={onToggle} />
      </Flex>

      <DefaultMobileMenu
        isOpen={isOpen}
        onClose={onClose}
        mobileLinks={mobileLinks as LinkWithLabel_T[]}
        onToggle={onToggle}
      />
    </>
  );
};

const DesktopNavContent = ({ buttons, links }: NavContent_T) => {
  const analytics = useAnalytics();
  return (
    <Flex flexDir={"column"}>
      <Flex
        bg={colors.enterpriseColors.blue["500"]}
        className="nav-content__desktop"
        key="content__desktop"
        align="center"
        justify="space-between"
        display={{
          base: "none",
          lg: "flex",
        }}
        h="72px"
        px={{
          lg: 10,
          xl: 16,
        }}
      >
        <LinkedLogo h="40px" />
        <HStack
          as="ul"
          key="nav__primary-menu"
          id="nav__primary-menu"
          aria-label="Main Menu"
          listStyleType="none"
          alignItems="center"
          justifyContent="flex-end"
          fontWeight="semibold"
          fontSize="16px"
          gap={"60px"}
          pl={10}
        >
          {links?.map(
            (
              { label, isInternal, industriesMenu, industriesMenus, ...rest },
              idx,
            ) => (
              <Flex
                as="li"
                key={`nav__primary-link-${idx}`}
                flexDir={"row"}
                align={"center"}
                h="72px"
                _hover={{
                  borderBottom: `3px solid ${colors.enterpriseColors.blue["100"]}`,
                }}
              >
                {label === "Resources" ? (
                  <IndustriesDropdownMenu
                    {...(industriesMenu as IndustriesMenu)}
                  />
                ) : label === "Solutions" ? (
                  <SolutionsDropdownMenu
                    label={label}
                    menus={industriesMenus as IndustriesMenu[]}
                  />
                ) : label === "Sign In" ? (
                  <Link
                    onClick={() => {
                      analytics.track(
                        "User clicked on a link on the Desktop Nav",
                        {
                          ui: "DesktopNavContent",
                          label: label,
                          requestedUrl: `${getWebUrl() + rest.href}`,
                        },
                      );
                    }}
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    color={"white"}
                    _hover={{
                      textDecor: "none",
                    }}
                    {...rest}
                  >
                    <IoMdPerson fill="white" fontSize={18} />
                    {label}
                  </Link>
                ) : (
                  <Link
                    onClick={() => {
                      analytics.track(
                        "User clicked on a link on the Desktop Nav",
                        {
                          ui: "DesktopNavContent",
                          label: label,
                          requestedUrl: `${getWebUrl() + rest.href}`,
                        },
                      );
                    }}
                    display="inline-block"
                    color={"white"}
                    _hover={{
                      textDecor: "none",
                    }}
                    {...rest}
                  >
                    {label}
                  </Link>
                )}
              </Flex>
            ),
          )}
          {buttons?.map(({ isInternal, ...btn }, idx) => (
            <Box as="li" key={`nav__primary-button-${idx}`}>
              <Button
                {...btn}
                width="180px"
                fontSize={"unset"}
                py={0}
                color={colors.enterpriseColors.blue["200"]}
                bg={"white"}
                onClick={() => {
                  analytics.track(`User clicked on a link on the Desktop Nav`, {
                    ui: "DesktopNavContent",
                    label: btn.label,
                    requestedUrl: `${
                      getWebUrl() + (btn as ButtonLink_T)?.href
                    }`,
                  });
                }}
                textTransform={"uppercase"}
              />
            </Box>
          ))}
        </HStack>
      </Flex>
    </Flex>
  );
};

export const NavContent = {
  Mobile: MobileNavContent,
  Desktop: DesktopNavContent,
};
